import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  OutlinedInput,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider,
  Switch,
  AlertTitle
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { formatOptions, getLabel } from './formatLabels';
import TableViewIcon from '@mui/icons-material/TableView';

// Import Redux actions
import {
  fetchAdvertiserList,
  fetchCampaignsForAdvertiser,
  fetchSkanCampaignsForAdvertiser,
  fetchJSTCampaignsForAdvertiser,
  setSelectedAdvertiser,
  fetchUserRoleByEmailId,
  clearMailerCampaigns,
  fetchMailerSkanCampaignsForAdvertiser,
  fetchMailerRegularCampaignsForAdvertiser,
  fetchMailerCombinedCampaignsForAdvertiser
} from '../../redux/advertiser/advertiserActions';

import { setMailerRecurrence, calculateMailerDateRange } from '../../redux/campaign/campaignActions';

import {
  fetchUserReportingEmailerDetails,
  fetchAllUserReportingEmailerDetails,
  fetchAllMeasures,
  fetchAllMetrics,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  toggleReportStatus
} from '../../redux/mailerlist/mailerActions';

const MailerList = ({
  // Redux props
  advertiserList,
  campaignList,
  loading,
  userEmail,
  advertiser_campaign_fetching,
  timeZone,

  // Mailer-specific campaign props
  mailerSkanCampaigns,
  mailerRegularCampaigns,
  mailerCombinedCampaigns,
  mailerSkanCampaignsLoading,
  mailerRegularCampaignsLoading,
  mailerCampaignsError,
  // Mailer Redux props
  userEmailerDetails,
  allUserEmailerDetails,
  mailerDataLoading,
  userEmailerDetailsLoading,
  allMeasures,
  allMetrics,
  createReportLoading,
  updateReportLoading,
  deleteReportLoading,
  toggleStatusLoading,

  // Actions
  fetchAdvertiserList,
  fetchCampaignsForAdvertiser,
  fetchSkanCampaignsForAdvertiser,
  fetchJSTCampaignsForAdvertiser,
  setSelectedAdvertiser,
  fetchUserRoleByEmailId,

  // Mailer-specific actions
  clearMailerCampaigns,
  fetchMailerCombinedCampaignsForAdvertiser,
  mailerStartDate,
  mailerEndDate,
  mailerRecurrence,
  setMailerRecurrence,

  fetchUserReportingEmailerDetails,
  fetchAllUserReportingEmailerDetails,
  fetchAllMeasures,
  fetchAllMetrics,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  toggleReportStatus,

  //Search props
  searchQuery
}) => {
  const dispatch = useDispatch();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    reportId: null,
    reportName: ''
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingReport, setEditingReport] = useState(null);
  const [editingUserEmail, setEditingUserEmail] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [fetchedCampaigns, setFetchedCampaigns] = useState([]); // set combine campaign for iOS and Android

  const filter = createFilterOptions();

  // Add memoized advertiser list for better performance
  const memoizedAdvertiserList = useMemo(() => {
    if (!advertiserList || advertiserList.length === 0) {
      return [];
    }
    return advertiserList.map((advertiser) => ({
      ...advertiser,
      // Add search-friendly properties
      searchText: advertiser.advertiser_name.toLowerCase()
    }));
  }, [advertiserList]);

  // Get user-specific mailer data
  const getUserMailerData = () => {
    if (userEmailerDetails && Array.isArray(userEmailerDetails)) {
      return userEmailerDetails;
    }
    return [];
  };

  // Format the options
  const formattedDimensions = useMemo(() => {
    return formatOptions(allMetrics || []);
  }, [allMetrics]);

  const formattedMeasures = useMemo(() => {
    return formatOptions(allMeasures || []);
  }, [allMeasures]);

  // Function to get minimum date (T+1)
  const getMinDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // FIXED: Helper function to reconstruct mail body from template
  const reconstructMailBody = (template) => {
    if (!template) {
      return 'Hi,\n\nPlease find the attached scheduled report for your review.\n\nBest regards,\nInmobi DSP Team';
    }

    const parts = [];
    if (template.header) parts.push(template.header);
    if (template.body) parts.push(template.body);
    if (template.footer) parts.push(template.footer);

    return parts.length > 0
      ? parts.join('\n\n')
      : 'Hi,\n\nPlease find the attached scheduled report for your review.\n\nBest regards,\nInmobi DSP Team';
  };

  // FIXED: Helper function to parse mail body into template parts
  const parseMailBody = (mailBody) => {
    const parts = mailBody.split('\n\n');

    if (parts.length === 1) {
      // Single paragraph - treat as body
      return {
        header: 'Hi,',
        body: parts[0],
        footer: 'Best regards,\nInmobi DSP Team'
      };
    } else if (parts.length === 2) {
      // Two paragraphs - header and footer
      return {
        header: parts[0],
        body: '',
        footer: parts[1]
      };
    } else if (parts.length >= 3) {
      // Three or more paragraphs - header, body(s), footer
      return {
        header: parts[0],
        body: parts.slice(1, -1).join('\n\n'),
        footer: parts[parts.length - 1]
      };
    }

    // Fallback
    return {
      header: 'Hi,',
      body: 'Please find the attached scheduled report for your review.',
      footer: 'Best regards,\nInmobi DSP Team'
    };
  };

  // Form state
  // ===== UPDATED FORM STATE WITH NEW FIELDS =====
  const [formData, setFormData] = useState({
    shareEmails: [],
    advertiser: null,
    selectedCampaigns: [],
    selectedDimensions: [],
    selectedMeasures: [],
    reportName: '',
    mailBody: 'Hi,\n\nPlease find the attached scheduled report for your review.\n\nBest regards,\nInmobi DSP Team',
    recurrence: 'Daily',
    selectedDays: [],
    startDateTime: dayjs(`${getMinDate()}T09:00`).format('YYYY-MM-DDTHH:mm'),
    // ===== NEW FIELDS =====
    customSubjectEnabled: false,
    customSubject: '',
    columnMappings: {},
    dateFormat: 'YYYY-MM-DD',
    rollupFrequency: 'Weekly', // Weekly or Monthly (used when recurrence === 'Rollup')
    includeRollup: false
  });

  const effectiveRecurrence = formData.recurrence === 'Rollup' ? formData.rollupFrequency : formData.recurrence;

  const filterReports = (reports, query) => {
    if (!query || query.trim() === '') {
      return reports;
    }

    const searchTerm = query.toLowerCase().trim();
    return reports.filter((report) => {
      // Search in report name
      const reportName = report.name?.toLowerCase() || '';

      // Search in user email (for AllMailerList)
      const userEmail = report.userEmail?.toLowerCase() || '';

      // Search in user name (for AllMailerList)
      const userName = report.userName?.toLowerCase() || '';

      // Search in advertiser name
      const advertiserName = getAdvertiserName(report.advertiser_guid)?.toLowerCase() || '';

      // Search in status
      const status = report.active ? 'active' : 'inactive';

      // Search in schedule frequency
      const frequency = report.schedule_time?.[3]?.toLowerCase() || '';

      return (
        reportName.includes(searchTerm) ||
        userEmail.includes(searchTerm) ||
        userName.includes(searchTerm) ||
        advertiserName.includes(searchTerm) ||
        status.includes(searchTerm) ||
        frequency.includes(searchTerm)
      );
    });
  };

  // Add this helper function to handle date selection
  const handleDateChange = (newValue) => {
    if (newValue) {
      // For Monthly, we only need the date part, set time to 09:00
      const formattedDateTime = newValue.format('YYYY-MM-DD') + 'T09:00';
      handleInputChange('startDateTime', formattedDateTime);
    }
  };

  const [emailInput, setEmailInput] = useState('');
  const [selectedAdvertiserForCampaigns, setSelectedAdvertiserForCampaigns] = useState(null);

  // Create a helper function to get the current campaign list
  const getCurrentCampaignList = () => {
    if (timeZone === 'JST') {
      // For JST timezone, use the existing campaignList (unchanged logic)
      return campaignList;
    } else {
      // For UTC timezone, use the new mailer combined campaigns
      return mailerCombinedCampaigns;
    }
  };

  // ===== NEW HELPER FUNCTIONS =====
  const formatDateWithFormat = useCallback((date, format) => {
    const d = dayjs(date);

    switch (format) {
      case 'DD/MM/YYYY':
        return d.format('DD/MM/YYYY');
      case 'MM/DD/YYYY':
        return d.format('MM/DD/YYYY');
      case 'DD-MMM-YYYY':
        return d.format('DD-MMM-YYYY');
      case 'MMM DD, YYYY':
        return d.format('MMM DD, YYYY');
      default:
        return d.format('YYYY-MM-DD');
    }
  });

  // Safe String Helper
  const toStr = (v, fallback = '') => {
    if (v === null || v === undefined) return fallback;
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') return v.label || v.name || v.value || JSON.stringify(v);
    return String(v);
  };

  const getLabel = (v, fallback = '') => {
    if (v == null) return fallback;
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      return v.label ?? v.name ?? v.value ?? fallback;
    }
    return String(v);
  };

  const dynamicPlaceholder = `${getLabel(formData.recurrence, '[Recurrence]')} Auto Mailer Report - ${getLabel(
    formData.reportName,
    'Your Report Name'
  )} - ${formatDateWithFormat(new Date(), formData.dateFormat || 'YYYY-MM-DD')}`;

  const previewSubject = () => {
    // If custom subject is disabled or empty, show default format
    if (!formData.customSubjectEnabled || !formData.customSubject) {
      const reportName = formData.reportName || formData.advertiser?.advertiser_name || 'Report';
      const today = formatDateWithFormat(new Date(), formData.dateFormat);
      return `${formData.recurrence} Auto Mailer Report - ${reportName} - ${today}`;
    }

    // Otherwise, just return the custom subject as-is
    return formData.customSubject;
  };

  const getSelectedColumns = () => {
    const columns = [];

    // Add selected dimensions with their labels
    formData.selectedDimensions.forEach((dim) => {
      const dimension = formattedDimensions.find((d) => d.value === dim);
      if (dimension) {
        columns.push({
          value: dim,
          label: dimension.label,
          type: 'dimension'
        });
      }
    });

    // Add selected measures with their labels
    formData.selectedMeasures.forEach((measure) => {
      const measureObj = formattedMeasures.find((m) => m.value === measure);
      if (measureObj) {
        columns.push({
          value: measure,
          label: measureObj.label,
          type: 'measure'
        });
      }
    });
    return columns;
  };

  // called on individual mapping change
  const handleColumnMapping = (field, displayName) => {
    setFormData((prev) => ({
      ...prev,
      columnMappings: {
        ...(prev.columnMappings || {}),
        [field]: displayName
      }
    }));
  };

  // reset all mappings for this report
  const resetColumnMappings = () => {
    setFormData((prev) => ({
      ...prev,
      columnMappings: {}
    }));
  };

  // validation - returns true if mappings are valid (no duplicate display names)
  const validateColumnMappings = (mappings) => {
    const names = Object.values(mappings || {})
      .filter(Boolean)
      .map((n) => String(n).trim());
    return names.length === new Set(names).size;
  };

  // Load advertiser list on component mount
  useEffect(() => {
    if (userEmail && advertiserList.length === 0) {
      fetchAdvertiserList(userEmail);
      fetchUserRoleByEmailId(userEmail);
    }
  }, [userEmail, advertiserList.length, fetchAdvertiserList, fetchUserRoleByEmailId]);

  // Load user-specific mailer data on component mount
  useEffect(() => {
    if (userEmail) {
      fetchUserReportingEmailerDetails(userEmail);
    }
    fetchAllMeasures();
    fetchAllMetrics();
  }, [userEmail, fetchUserReportingEmailerDetails, fetchAllMeasures, fetchAllMetrics]);

  // Add this for debugging
  useEffect(() => {
    // console.log('=== CAMPAIGN DEBUG ===');
    // console.log('timeZone:', timeZone);
    // console.log('campaignList (JST):', campaignList);
    // console.log('mailerCombinedCampaigns (UTC):', mailerCombinedCampaigns);
    // console.log('mailerSkanCampaigns:', mailerSkanCampaigns);
    // console.log('mailerRegularCampaigns:', mailerRegularCampaigns);
    // console.log('currentCampaigns:', getCurrentCampaignList());
  }, [timeZone, campaignList, mailerCombinedCampaigns, mailerSkanCampaigns, mailerRegularCampaigns]);

  const handleAdvertiserChange = useCallback(
    (event, value) => {
      setFormData((prev) => ({
        ...prev,
        advertiser: value,
        selectedCampaigns: [] // Clear selected campaigns
      }));

      setSelectedAdvertiserForCampaigns(value);

      if (value) {
        if (timeZone === 'JST') {
          // Keep existing JST logic unchanged
          fetchJSTCampaignsForAdvertiser(value.advertiser_guid, value.salesforce_account_id);
        } else {
          // For UTC, use new mailer-specific combined fetch
          fetchMailerCombinedCampaignsForAdvertiser(value.advertiser_guid, value.salesforce_account_id, mailerStartDate, mailerEndDate);
        }
      } else {
        // Clear campaigns if no advertiser selected
        if (timeZone !== 'JST') {
          clearMailerCampaigns();
        }
      }
    },
    [
      timeZone,
      fetchJSTCampaignsForAdvertiser,
      fetchMailerCombinedCampaignsForAdvertiser,
      clearMailerCampaigns,
      mailerStartDate,
      mailerEndDate
    ]
  );

  const isLoadingCampaigns =
    timeZone === 'JST' ? advertiser_campaign_fetching : mailerSkanCampaignsLoading || mailerRegularCampaignsLoading;

  // Update the campaign selection JSX to use the correct campaign list
  const currentCampaigns = getCurrentCampaignList();

  // ===== UPDATED useEffect for EDIT MODE =====
  useEffect(() => {
    if (modalMode === 'edit' && editingReport) {
      const freq = (editingReport.schedule_time?.[3] || 'daily').toLowerCase();
      const advertiser = advertiserList.find((adv) => adv.advertiser_guid === editingReport.advertiser_guid);

      if (advertiser) {
        setSelectedAdvertiserForCampaigns(advertiser);
        if (timeZone === 'JST') {
          fetchJSTCampaignsForAdvertiser(advertiser.advertiser_guid, advertiser.salesforce_account_id);
        } else {
          fetchMailerCombinedCampaignsForAdvertiser(advertiser.advertiser_guid, advertiser.salesforce_account_id);
        }
      }

      setFormData({
        shareEmails: [...editingReport.receiptants.to],
        advertiser: advertiser,
        selectedCampaigns: [...editingReport.campaign_guid_list],
        selectedDimensions: [...editingReport.metrics],
        selectedMeasures: [...editingReport.measures],
        reportName: editingReport.name,
        mailBody: reconstructMailBody(editingReport.template),
        recurrence: editingReport.include_rollup ? 'Rollup' : freq.charAt(0).toUpperCase() + freq.slice(1),
        rollupFrequency: editingReport.include_rollup
          ? freq.charAt(0).toUpperCase() + freq.slice(1) // Weekly / Monthly rollup
          : 'Weekly', // default when non-rollup
        selectedDays: editingReport.schedule_time[1] ? editingReport.schedule_time[1].split(',') : [],
        startDateTime: (() => {
          const scheduleTime = editingReport.schedule_time;
          const [time, days, timezone, frequency] = scheduleTime;

          if (frequency === 'monthly') {
            const existingDate = editingReport.schedule_date || getMinDate();
            return dayjs(`${existingDate}T${time}`).format('YYYY-MM-DDTHH:mm');
          } else {
            return dayjs(`${getMinDate()}T${time}`).format('YYYY-MM-DDTHH:mm');
          }
        })(),
        // ===== NEW FIELDS FOR EDIT MODE =====
        customSubjectEnabled: editingReport.custom_subject ? true : false,
        customSubject: editingReport.custom_subject || '',
        columnMappings: editingReport.column_mappings || {},
        dateFormat: editingReport.date_format || 'YYYY-MM-DD',
        includeRollup: !!editingReport.include_rollup
      });
    } else {
      // Reset form for create mode
      const minDate = getMinDate();
      setFormData({
        shareEmails: [],
        advertiser: null,
        selectedCampaigns: [],
        selectedDimensions: [],
        selectedMeasures: [],
        reportName: '',
        mailBody: 'Hi,\n\nPlease find the attached scheduled report for your review.\n\nBest regards,\nInmobi DSP Team',
        recurrence: 'Daily',
        selectedDays: [],
        startDateTime: `${minDate}T09:00`,
        // ===== NEW FIELDS RESET =====
        customSubjectEnabled: false,
        customSubject: '',
        columnMappings: {},
        dateFormat: 'YYYY-MM-DD',
        rollupFrequency: 'Weekly',
        includeRollup: false
      });
      setSelectedAdvertiserForCampaigns(null);
    }
  }, [
    modalMode,
    editingReport,
    advertiserList,
    timeZone,
    fetchCampaignsForAdvertiser,
    fetchJSTCampaignsForAdvertiser,
    fetchSkanCampaignsForAdvertiser,
    fetchMailerCombinedCampaignsForAdvertiser
  ]);
  // Format schedule time for display
  const formatScheduleTime = (scheduleTime, includeRollup) => {
    if (!scheduleTime || scheduleTime.length < 4) return 'Invalid schedule';

    const [time, days, timezone, frequency] = scheduleTime;
    const freqLabel = frequency.charAt(0).toUpperCase() + frequency.slice(1);
    // Base string always shown
    let result = `${time} ${timezone} - ${freqLabel}`;

    // For NON–rollup reports → show days or schedule date
    if (!includeRollup) {
      const daysList = days
        ? days
            .split(',')
            .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
            .join(', ')
        : '';

      result += ` (${daysList})`;
    }

    // For rollup reports → add Rollup icon only
    if (includeRollup) {
      result += ` Rollup 🔁`;
    }

    return result;
  };

  // Function to check if a date should be disabled
  const shouldDisableDate = (date) => {
    const today = dayjs();
    const dateToCheck = dayjs(date);
    return dateToCheck.isBefore(today, 'day');
  };

  // Function to check if a time should be disabled for today
  const shouldDisableTime = (value, view) => {
    if (!value) return false;
    const now = dayjs();
    const selectedDate = dayjs(value);

    if (selectedDate.isSame(now, 'day') && view === 'hours') {
      const selectedHour = dayjs(value).hour();
      const currentHour = now.hour();
      return selectedHour <= currentHour;
    }
    return false;
  };

  // Get user name from email
  const getUserName = (email) => {
    return email
      .split('@')[0]
      .replace('.', ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get advertiser name from advertiser list
  const getAdvertiserName = (advertiser_guid) => {
    const advertiser = advertiserList.find((adv) => adv.advertiser_guid === advertiser_guid);
    return advertiser ? advertiser.advertiser_name : 'Unknown Advertiser';
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingReport(null);
    setEditingUserEmail(null);
    setIsModalOpen(true);
  };

  const openEditModal = (report) => {
    setModalMode('edit');
    setEditingReport(report);
    setEditingUserEmail(userEmail);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalMode('create');
    setEditingReport(null);
    setEditingUserEmail(null);
    setEmailInput('');
    setSelectedAdvertiserForCampaigns(null);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleAddEmail = () => {
    if (emailInput && validateEmail(emailInput) && !formData.shareEmails.includes(emailInput)) {
      setFormData((prev) => ({
        ...prev,
        shareEmails: [...prev.shareEmails, emailInput]
      }));
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      shareEmails: prev.shareEmails.filter((email) => email !== emailToRemove)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Add handler for recurrence change
  const handleRecurrenceChange = useCallback(
    (newRecurrence) => {
      let baseRecurrence = newRecurrence;

      let nextRollupFreq = formData.rollupFrequency || 'Weekly';

      // If switching to Rollup, base recurrence is the rollup type (Weekly/Monthly)
      if (newRecurrence === 'Rollup') {
        baseRecurrence = nextRollupFreq;
      }
      setFormData((prev) => {
        const rollFreq = prev.rollupFrequency || 'Weekly';
        return {
          ...prev,
          recurrence: newRecurrence,
          rollupFrequency: newRecurrence === 'Rollup' ? rollFreq : prev.rollupFrequency,
          includeRollup: newRecurrence === 'Rollup',
          selectedCampaigns: [],
          // for Weekly Rollup -> all days selected, otherwise clear
          selectedDays: newRecurrence === 'Rollup' && rollFreq === 'Weekly' ? ALL_WEEK_DAYS : newRecurrence === 'Weekly' ? [] : []
        };
      });

      // Calculate new date range based on recurrence
      const dateRange = calculateMailerDateRange(baseRecurrence);

      dispatch(setMailerRecurrence(baseRecurrence));

      // Re-fetch campaigns with new date range if advertiser is selected
      if (formData.advertiser && timeZone !== 'JST') {
        fetchMailerCombinedCampaignsForAdvertiser(
          formData.advertiser.advertiser_guid,
          formData.advertiser.salesforce_account_id,
          dateRange.startDate,
          dateRange.endDate
        );
      }
    },
    [formData.advertiser, timeZone, dispatch, fetchMailerCombinedCampaignsForAdvertiser]
  );

  const handleMultiSelect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter((item) => item !== value) : [...prev[field], value]
    }));
  };

  const isWeeklyRollup = formData.recurrence === 'Rollup' && formData.rollupFrequency === 'Weekly';

  const handleDayToggle = (day) => {
    if (effectiveRecurrence !== 'Weekly') return;

    // In Weekly Rollup user should NOT change days
    if (isWeeklyRollup) return;

    // Normal Weekly – keep old single-select behavior
    if (formData.recurrence === 'Weekly') {
      setFormData((prev) => ({
        ...prev,
        selectedDays: prev.selectedDays.includes(day) ? prev.selectedDays.filter((d) => d !== day) : [day]
      }));
    }
  };

  const isFormValid = () => {
    const baseValidation =
      formData.shareEmails.length > 0 &&
      formData.advertiser &&
      formData.selectedCampaigns.length > 0 &&
      // formData.selectedDimensions.length > 0 &&
      formData.selectedMeasures.length > 0 &&
      formData.reportName.trim();

    // Additional validation based on recurrence type
    if (effectiveRecurrence === 'Weekly') {
      return baseValidation && formData.selectedDays.length > 0;
    } else if (effectiveRecurrence === 'Monthly') {
      return baseValidation && formData.startDateTime;
    }

    // For Daily, no additional validation needed
    return baseValidation;
  };

  // Updated: Handle submit with proper template parsing
  const handleSubmit = () => {
    if (!isFormValid()) {
      alert('Please fill all required fields');
      return;
    }

    // ==== Validate column mappings before saving ====
    if (formData.columnMappings && Object.keys(formData.columnMappings).length > 0) {
      // Trim mapping values to avoid accidental duplicates with trailing spaces
      const trimmedMappings = Object.fromEntries(Object.entries(formData.columnMappings).map(([k, v]) => [k, (v || '').toString().trim()]));

      if (!validateColumnMappings(trimmedMappings)) {
        alert('Duplicate display names found in column header customizations. Please ensure unique names.');
        return;
      }
      // replace formData.columnMappings with trimmed version for submission
      formData.columnMappings = trimmedMappings;
    }

    const targetUserEmail = modalMode === 'create' ? userEmail : editingUserEmail;

    let scheduleTime;
    const baseRecurrence = effectiveRecurrence; // Daily / Weekly / Monthly
    if (baseRecurrence === 'Monthly') {
      const dateTime = dayjs(formData.startDateTime);
      scheduleTime = ['09:00', dateTime.format('DD'), 'UTC', 'monthly'];
    } else if (baseRecurrence === 'Weekly') {
      scheduleTime = ['09:00', formData.selectedDays.join(',').toLowerCase(), 'UTC', 'weekly'];
    } else {
      scheduleTime = ['09:00', '', 'UTC', 'daily'];
    }
    const reportData = {
      email_id: targetUserEmail,
      report_details: [
        {
          name: formData.reportName,
          metrics: formData.selectedDimensions,
          measures: formData.selectedMeasures,
          receiptants: {
            to: formData.shareEmails,
            from: targetUserEmail
          },
          advertiser_guid: formData.advertiser.advertiser_guid,
          advertiser_name: formData.advertiser.advertiser_name,
          campaign_guid_list: formData.selectedCampaigns,
          sfid: formData.advertiser.salesforce_account_id,
          schedule_time: scheduleTime,
          ...(baseRecurrence === 'Monthly' && {
            schedule_date: dayjs(formData.startDateTime).format('YYYY-MM-DD')
          }),
          template: parseMailBody(formData.mailBody),
          active: true,
          // ===== NEW FIELDS IN SUBMISSION =====
          custom_subject: formData.customSubjectEnabled ? formData.customSubject : null,
          column_mappings: Object.keys(formData.columnMappings).length > 0 ? formData.columnMappings : null,
          date_format: formData.dateFormat,
          include_rollup: formData.recurrence === 'Rollup'
        }
      ]
    };

    if (modalMode === 'create') {
      createScheduledReport(reportData);
    } else {
      updateScheduledReport(userEmail, editingReport.name, reportData);
    }

    closeModal();
  };

  const handleEdit = (report) => {
    openEditModal(report);
  };

  const handleToggleActive = (report) => {
    toggleReportStatus(userEmail, report.name, !report.active);
  };

  const handleDeleteClick = (report) => {
    setDeleteDialog({
      open: true,
      reportId: report.name,
      reportName: report.name
    });
  };

  const handleDeleteConfirm = () => {
    const { reportId } = deleteDialog;
    deleteScheduledReport(userEmail, reportId);
    setDeleteDialog({ open: false, reportId: null, reportName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, reportId: null, reportName: '' });
  };

  // Get user's scheduled reports
  const getScheduledReports = () => {
    const userMailerData = getUserMailerData();
    let reports = [];

    if (userMailerData && userMailerData.length > 0) {
      const userReports = userMailerData.find((user) => user.email_id === userEmail);
      reports = userReports ? userReports.scheduled_reports || [] : [];
    }
    // Apply search filter
    return filterReports(reports, searchQuery);
  };

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const weekDaysFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const ALL_WEEK_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  if (loading || userEmailerDetailsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <Typography>Loading scheduled reports...</Typography>
      </Box>
    );
  }

  const scheduledReports = getScheduledReports();

  // console.log('customSubject:', formData?.customSubject);
  // console.log('reportName:', formData?.reportName);
  // console.log('recurrence:', formData?.recurrence);
  // console.log('previewSubject():', previewSubject && previewSubject());

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header with Schedule Report Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 3
          }}
        >
          {/* <Typography variant="h4" gutterBottom>
              My Scheduled Reports
            </Typography> */}
          <Button
            variant="contained"
            color="error"
            onClick={openCreateModal}
            disabled={createReportLoading}
            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
          >
            {createReportLoading ? 'Creating...' : '+ Schedule Report'}
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>
                  <strong>Report Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Scheduled Time</strong>
                </TableCell>
                <TableCell>
                  <strong>Advertiser Name</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No scheduled reports found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                scheduledReports.map((report, index) => (
                  <TableRow key={`${report.name}-${index}`} hover>
                    <TableCell>
                      <Typography variant="body2">{report.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatScheduleTime(report.schedule_time, report.include_rollup)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getAdvertiserName(report.advertiser_guid)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={report.active ? 'Active' : 'Inactive'}
                        color={report.active ? 'success' : 'default'}
                        size="small"
                        variant={report.active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(report)}
                          disabled={updateReportLoading}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color={report.active ? 'warning' : 'success'}
                          size="small"
                          startIcon={report.active ? <PowerSettingsNewIcon /> : <CheckCircleIcon />}
                          onClick={() => handleToggleActive(report)}
                          disabled={toggleStatusLoading}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            borderColor: report.active ? '#ff9800' : '#4caf50',
                            color: report.active ? '#ff9800' : '#4caf50',
                            '&:hover': {
                              borderColor: report.active ? '#f57c00' : '#388e3c',
                              backgroundColor: report.active ? 'rgba(255, 152, 0, 0.04)' : 'rgba(76, 175, 80, 0.04)'
                            }
                          }}
                        >
                          {report.active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(report)}
                          disabled={deleteReportLoading}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal for Schedule/Edit Report */}
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { maxHeight: '90vh' }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {modalMode === 'create' ? 'Schedule Report' : 'Update Report Settings'}
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pb: 2 }}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Share Emails */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Share Emails *
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Enter email address"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <Button variant="contained" onClick={handleAddEmail} disabled={!emailInput}>
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.shareEmails.map((email) => (
                      <Chip key={email} label={email} onDelete={() => handleRemoveEmail(email)} variant="outlined" />
                    ))}
                  </Box>
                </Grid>

                {/* Recurrence */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Recurrence *
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup row value={formData.recurrence} onChange={(e) => handleRecurrenceChange(e.target.value)}>
                      <FormControlLabel value="Daily" control={<Radio />} label="Daily" />
                      <FormControlLabel value="Weekly" control={<Radio />} label="Weekly" />
                      <FormControlLabel value="Monthly" control={<Radio />} label="Monthly" />
                      <FormControlLabel value="Rollup" control={<Radio />} label="Rollup Summary" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* ===== NEW: ROLLUP DATA OPTION ===== */}
                {formData.recurrence === 'Rollup' && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Rollup Summary
                      </Typography>

                      {/* choose Weekly vs Monthly rollup */}
                      <RadioGroup
                        row
                        value={formData.rollupFrequency}
                        onChange={(e) =>
                          setFormData((prev) => {
                            const newFreq = e.target.value; // 'Weekly' or 'Monthly'

                            return {
                              ...prev,
                              rollupFrequency: newFreq,
                              // Weekly Rollup -> all days, Monthly Rollup -> no days
                              selectedDays: newFreq === 'Weekly' ? ALL_WEEK_DAYS : [],
                              startDateTime: prev.startDateTime
                            };
                          })
                        }
                      >
                        <FormControlLabel value="Weekly" control={<Radio />} label="Weekly Rollup" />
                        <FormControlLabel value="Monthly" control={<Radio />} label="Monthly Rollup" />
                      </RadioGroup>

                      <Alert severity="info" sx={{ mt: 2 }}>
                        <AlertTitle>Rollup Details</AlertTitle>
                        <Typography variant="body2">
                          • Daily breakdown data (standard report)
                          <br />• Coverage:{' '}
                          {formData.rollupFrequency === 'Weekly'
                            ? 'Last 7 days from the selected day'
                            : 'Current month from 1st to yesterday'}
                        </Typography>
                      </Alert>
                    </Paper>
                  </Grid>
                )}

                {/* Days Selection for Weekly */}
                {effectiveRecurrence === 'Weekly' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Select Days *
                      {isWeeklyRollup && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (Fixed for Weekly Rollup – all days)
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {weekDaysFull.map((day, index) => {
                        const dayKey = day.toLowerCase();
                        const selected = formData.selectedDays.includes(dayKey);

                        return (
                          <Button
                            key={day}
                            variant={selected ? 'contained' : 'outlined'}
                            size="small"
                            disabled={isWeeklyRollup} // cannot click in Weekly Rollup
                            onClick={isWeeklyRollup ? undefined : () => handleDayToggle(dayKey)}
                          >
                            {weekDays[index]}
                          </Button>
                        );
                      })}
                    </Box>
                  </Grid>
                )}

                {/* START DATE SELECTION - Only for Monthly */}
                {effectiveRecurrence === 'Monthly' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Select Start Date *
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        value={dayjs(formData.startDateTime)}
                        onChange={handleDateChange}
                        shouldDisableDate={shouldDisableDate}
                        views={['year', 'month', 'day']} // Only show date picker, no time
                        format="YYYY-MM-DD"
                        minDate={dayjs()} // Minimum date is today
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            helperText="Select the date when the monthly report should start"
                            error={effectiveRecurrence === 'Monthly' && !formData.startDateTime}
                          />
                        )}
                        slotProps={{
                          textField: {
                            helperText: 'Select the date when the monthly report should start',
                            error: effectiveRecurrence === 'Monthly' && !formData.startDateTime
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                )}

                {/* Advertiser Selection */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Advertiser *
                  </Typography>
                  <Autocomplete
                    options={memoizedAdvertiserList}
                    getOptionLabel={(option) => option?.advertiser_name || ''}
                    value={formData.advertiser}
                    onChange={handleAdvertiserChange}
                    disabled={modalMode === 'edit'}
                    loading={loading}
                    isOptionEqualToValue={(option, value) => option?.advertiser_guid === value?.advertiser_guid}
                    onInputChange={(event, newInputValue) => {
                      setSearchInput(newInputValue);
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type to search advertisers..."
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.advertiser_guid}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(25, 118, 210, 0.12)'
                          },
                          padding: '8px 16px',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                          '&:last-child': {
                            borderBottom: 'none'
                          }
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: selected ? 600 : 400,
                            color: selected ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {option.advertiser_name}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText="No advertisers found matching your search"
                    loadingText="Loading advertisers..."
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    openOnFocus
                    blurOnSelect={false}
                    clearOnBlur={false}
                    ListboxProps={{
                      style: {
                        maxHeight: '200px',
                        overflow: 'auto'
                      }
                    }}
                    PaperComponent={({ children, ...other }) => (
                      <Paper
                        {...other}
                        elevation={8}
                        sx={{
                          marginTop: '4px',
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          '& .MuiAutocomplete-listbox': {
                            padding: 0
                          }
                        }}
                      >
                        {children}
                      </Paper>
                    )}
                  />
                </Grid>

                {/* Campaign Selection */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Campaigns *
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Select campaigns</InputLabel>
                    <Select
                      multiple
                      value={formData.selectedCampaigns}
                      onChange={(e) => handleInputChange('selectedCampaigns', e.target.value)}
                      input={<OutlinedInput label="Select campaigns" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const campaign = currentCampaigns.find((c) => c.campaign_guid === value);
                            return <Chip key={value} label={campaign ? campaign.campaign_name : value} />;
                          })}
                        </Box>
                      )}
                    >
                      {currentCampaigns.map((campaign) => (
                        <MenuItem key={campaign.campaign_guid} value={campaign.campaign_guid}>
                          {campaign.campaign_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Dimensions and Measures */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Dimensions </InputLabel>
                    <Select
                      multiple
                      value={formData.selectedDimensions}
                      onChange={(e) => handleInputChange('selectedDimensions', e.target.value)}
                      input={<OutlinedInput label="Select Dimensions" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={getLabel(value, formattedDimensions)} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {formattedDimensions.map((dimension) => (
                        <MenuItem key={dimension.value} value={dimension.value}>
                          {dimension.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Measures </InputLabel>
                    <Select
                      multiple
                      value={formData.selectedMeasures}
                      onChange={(e) => handleInputChange('selectedMeasures', e.target.value)}
                      input={<OutlinedInput label="Select Measures" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={getLabel(value, formattedMeasures)} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {formattedMeasures.map((measure) => (
                        <MenuItem key={measure.value} value={measure.value}>
                          {measure.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* ===== Customize column headers (per selected measure/dimension) ===== */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      // limit the mapping area height so dialog doesn't grow too tall
                      maxHeight: '30vh',
                      overflow: 'auto'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Customize column headers (optional)
                      </Typography>
                      <Button size="small" onClick={resetColumnMappings}>
                        Reset all
                      </Button>
                    </Box>
                    {/* ===== Preview row ===== */}
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TableViewIcon fontSize="small" sx={{ opacity: 0.8 }} />
                      Preview headers:{' '}
                      {getSelectedColumns()
                        .map((c) => formData.columnMappings?.[c.value] || c.label)
                        .join(' | ')}
                    </Typography>

                    {/* Dimensions */}
                    {formData.selectedDimensions?.length > 0 && (
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Dimensions
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {formData.selectedDimensions.map((field) => {
                            const defaultLabel = getLabel(field, formattedDimensions);
                            const value = (formData.columnMappings && formData.columnMappings[field]) ?? defaultLabel;
                            return (
                              <Box key={field} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography sx={{ minWidth: 180 }}>{defaultLabel}</Typography>
                                <TextField
                                  size="small"
                                  placeholder="Display name"
                                  value={toStr(value, '')}
                                  onChange={(e) => handleColumnMapping(field, e.target.value)}
                                  sx={{ flex: 1 }}
                                />
                                <Button size="small" onClick={() => handleColumnMapping(field, defaultLabel)}>
                                  Reset
                                </Button>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    {/* Measures */}
                    {formData.selectedMeasures?.length > 0 && (
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Measures
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {formData.selectedMeasures.map((field) => {
                            const defaultLabel = getLabel(field, formattedMeasures);
                            const value = (formData.columnMappings && formData.columnMappings[field]) ?? defaultLabel;
                            return (
                              <Box key={field} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography sx={{ minWidth: 180 }}>{defaultLabel}</Typography>
                                <TextField
                                  size="small"
                                  placeholder="Display name"
                                  value={toStr(value, '')}
                                  onChange={(e) => handleColumnMapping(field, e.target.value)}
                                  sx={{ flex: 1 }}
                                />
                                <Button size="small" onClick={() => handleColumnMapping(field, defaultLabel)}>
                                  Reset
                                </Button>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Report Name */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Report Name *
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.reportName}
                    onChange={(e) => handleInputChange('reportName', e.target.value)}
                    placeholder="Enter report name"
                  />
                </Grid>

                {/* ===== NEW: CUSTOMISATION ===== */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <FormControlLabel
                      control={
                        // eslint-disable-next-line react/jsx-no-undef
                        <Switch
                          checked={formData.customSubjectEnabled}
                          onChange={(e) => handleInputChange('customSubjectEnabled', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={<Typography variant="subtitle1">Customize Subject Name and Date format for your Report:</Typography>}
                    />

                    {formData.customSubjectEnabled && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          value={toStr(formData?.customSubject, '')}
                          onChange={(e) => handleInputChange('customSubject', e.target.value && String(e.target.value))}
                          placeholder={dynamicPlaceholder}
                          helperText="Enter custom subject. If left empty, default format will be used: [Recurrence] Auto Mailer Report - [Report Name] - [Date]"
                        />
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Email Subject Preview:</strong>
                            <br />
                            {toStr(
                              formData?.customSubject && Object.keys(formData.customSubject).length
                                ? formData.customSubject
                                : previewSubject
                                ? previewSubject()
                                : `${getLabel(formData?.recurrence, '[Recurrence]')} Auto Mailer Report - ${getLabel(
                                    formData?.reportName,
                                    'Your Report Name'
                                  )} - ${formatDateWithFormat(new Date(), formData?.dateFormat || 'YYYY-MM-DD')}`
                            )}
                          </Typography>
                          {/* {!formData.customSubject && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Default format: {formData.recurrence} Auto Mailer Report - {formData.reportName || '[Report Name]'} - {formatDateWithFormat(new Date(), formData.dateFormat)}
                            </Typography>
                          )} */}
                        </Alert>
                        {/* ===== DATE FORMAT SELECTOR ===== */}
                        {/* <Typography variant="subtitle1" gutterBottom  sx={{ mt: 2}}>
                          Date Format for Report
                        </Typography> */}
                        <FormControl fullWidth sx={{ mt: 2 }}>
                          <InputLabel>Select Date Format</InputLabel>
                          <Select
                            value={formData.dateFormat}
                            onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                            input={<OutlinedInput label="Select Date Format" />}
                          >
                            <MenuItem value="YYYY-MM-DD">
                              YYYY-MM-DD (Default) - Ex:{toStr(formatDateWithFormat(new Date(), 'YYYY-MM-DD'))}
                            </MenuItem>
                            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY - Ex:{toStr(formatDateWithFormat(new Date(), 'DD/MM/YYYY'))}</MenuItem>
                            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY - Ex:{toStr(formatDateWithFormat(new Date(), 'MM/DD/YYYY'))}</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Mail Body */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Mail Body
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.mailBody}
                    onChange={(e) => handleInputChange('mailBody', e.target.value)}
                    placeholder="Enter email body"
                  />
                </Grid>

                {/* Information Notes Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{
                      mt: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Schedule Information:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        • <strong>Daily:</strong> Auto mailer will be sent daily for T-1 day for a scheduled mailer.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        • <strong>Weekly:</strong> Auto mailer will be sent for last 7 days for selected week of the day.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Monthly:</strong> Auto mailer will be sent for last month, on selected day of current month.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        • <strong>Rollup Summary:</strong> Aggregated report (weekly or monthly) plus daily breakdown for the same period.
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        *This Report is combined report for Android and iOS
                      </Typography>
                    </Box>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeModal} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!isFormValid() || createReportLoading || updateReportLoading}
            >
              {createReportLoading || updateReportLoading
                ? 'Processing...'
                : modalMode === 'create'
                ? 'Schedule Report'
                : 'Update Report Settings'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the report &quot;
              {deleteDialog.reportName}&quot;? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteReportLoading}>
              {deleteReportLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

// Updated mapStateToProps to handle the proper data structure
const mapStateToProps = (state) => {
  return {
    // Advertiser Redux state
    advertiserList: state.advertiserReducer.advertiser_list,
    campaignList: state.advertiserReducer.campaign_list,
    loading: state.advertiserReducer.loading,
    userEmail: state.userReducer.userEmail,
    advertiser_campaign_fetching: state.advertiserReducer.advertiser_campaign_fetching,
    timeZone: state.advertiserReducer.timeZone,
    campaign_list_error_msg: state.advertiserReducer.campaign_list_error_msg,

    // Mailer-specific campaign props
    mailerSkanCampaigns: state.advertiserReducer.mailer_skan_campaigns,
    mailerRegularCampaigns: state.advertiserReducer.mailer_regular_campaigns,
    mailerCombinedCampaigns: state.advertiserReducer.mailer_combined_campaigns,
    mailerSkanCampaignsLoading: state.advertiserReducer.mailer_skan_campaigns_loading,
    mailerRegularCampaignsLoading: state.advertiserReducer.mailer_regular_campaigns_loading,
    mailerCampaignsError: state.advertiserReducer.mailer_campaigns_error,
    mailerStartDate: state.campaignReducer.mailer_start_date,
    mailerEndDate: state.campaignReducer.mailer_end_date,
    mailerRecurrence: state.campaignReducer.mailer_recurrence,

    // Mailer Redux state - Updated to use user-specific data
    userEmailerDetails: state.mailerReducer.userEmailerDetails,
    allUserEmailerDetails: state.mailerReducer.allUserEmailerDetails,
    mailerDataLoading: state.mailerReducer.allEmailerDetailsLoading,
    userEmailerDetailsLoading: state.mailerReducer.userEmailerDetailsLoading,
    allMeasures: state.mailerReducer.allMeasures,
    allMetrics: state.mailerReducer.allMetrics,
    createReportLoading: state.mailerReducer.createReportLoading,
    updateReportLoading: state.mailerReducer.updateReportLoading,
    deleteReportLoading: state.mailerReducer.deleteReportLoading,
    toggleStatusLoading: state.mailerReducer.toggleStatusLoading
  };
};

const mapDispatchToProps = {
  fetchAdvertiserList,
  fetchCampaignsForAdvertiser,
  fetchSkanCampaignsForAdvertiser,
  fetchJSTCampaignsForAdvertiser,
  setSelectedAdvertiser,
  fetchUserRoleByEmailId,

  clearMailerCampaigns,
  fetchMailerSkanCampaignsForAdvertiser,
  fetchMailerRegularCampaignsForAdvertiser,
  fetchMailerCombinedCampaignsForAdvertiser,
  setMailerRecurrence,

  fetchUserReportingEmailerDetails,
  fetchAllUserReportingEmailerDetails,
  fetchAllMeasures,
  fetchAllMetrics,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  toggleReportStatus
};

export default connect(mapStateToProps, mapDispatchToProps)(MailerList);
